/**
 * Image URL utility functions
 * Handles conversion between relative and absolute image URLs
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Converts an image URL to a full absolute URL
 * Handles: relative paths (/uploads/...), old domain URLs, and already correct URLs
 * @param {string} url - The image URL to convert
 * @returns {string} - The full absolute URL
 */
export const getFullImageUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL with the current backend domain, return as-is
  if (url.startsWith(BACKEND_URL)) {
    return url;
  }
  
  // If it's a relative path (starts with /uploads/), prepend backend URL with /api prefix
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  
  // If it's a full URL to a different domain (old preview URLs or external images)
  // Check if it's an old preview URL that we can fix
  if (url.includes('.preview.emergentagent.com/uploads/')) {
    // Extract the relative path from old preview URLs
    const match = url.match(/\/uploads\/[^?#]+/);
    if (match) {
      return `${BACKEND_URL}/api${match[0]}`;
    }
  }
  
  // For external URLs (unsplash, pexels, etc.), return as-is
  return url;
};

/**
 * Converts an array of image URLs to full absolute URLs
 * @param {string[]} urls - Array of image URLs
 * @returns {string[]} - Array of full absolute URLs
 */
export const getFullImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  return urls.map(getFullImageUrl).filter(Boolean);
};

/**
 * Extracts the relative path from a full image URL for storage
 * @param {string} url - The full image URL
 * @returns {string} - The relative path or original URL if external
 */
export const getRelativeImagePath = (url) => {
  if (!url) return null;
  
  // If it's already a relative path, return as-is
  if (url.startsWith('/uploads/')) {
    return url;
  }
  
  // If it contains our upload path, extract it
  const match = url.match(/\/uploads\/[^?#]+/);
  if (match) {
    return match[0];
  }
  
  // For external URLs, return as-is
  return url;
};
